import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, tap, throwError } from 'rxjs';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import {
  CreateProformSubmissionAnswerPayload,
  CreateProformSubmissionPayload,
} from '../../types/payloads/proforms.payloads';
import {
  ProFormContext,
  ProformFieldTypes,
  ProFormsSubmissionAPIResponse,
} from '../../types/responses/proforms.responses';
import { AlertService } from '../alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class ProformsSubmissionService {
  // Current submission - when viewing an already submitted form
  private CurrentSubmissionData: ProFormsSubmissionAPIResponse | null = null;

  private CurrentSubmissionSource = new BehaviorSubject(
    this.CurrentSubmissionData
  );

  public currentSubmission = this.CurrentSubmissionSource.asObservable();

  // New submission - when filling out a form, keeps track of selected values
  private NewSubmissionData: CreateProformSubmissionPayload | null = null;

  private NewSubmissionSource = new BehaviorSubject(this.NewSubmissionData);

  public newSubmission = this.NewSubmissionSource.asObservable();

  constructor(
    private apiService: APICoreService,
    private alertService: AlertService,
    protected httpClient: HttpClient
  ) {}

  getProForm(formId: number) {
    return this.apiService
      .getRequest(
        `growelab/pro-forms/forms/${formId}?expand=sections.questions.answerOptions`
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  getSubmission(submissionId: number) {
    return this.apiService
      .getRequest(
        `growelab/pro-forms/form-submissions/${submissionId}?expand=answers,answers.fileInfo,answers.selectedCell,answers.answerAttachment`
      )
      .subscribe({
        error: (error) => {
          this.CurrentSubmissionSource.next(null);
          this.alertService.showAlert(`${error.error.message}`);
        },
        next: (response) => {
          if (response.item) {
            this.CurrentSubmissionData = response.item;
            this.CurrentSubmissionSource.next(this.CurrentSubmissionData);
          } else {
            this.CurrentSubmissionSource.next(null);
          }
        },
      });
  }

  getAllSubmissions(
    formId: number,
    contextTable?: string,
    contextTableId?: number
  ) {
    const params: {
      per_page: number;
      context_table?: string;
      context_table_id?: number;
    } = {
      per_page: 1000,
    };

    if (contextTable) {
      params.context_table = contextTable;
    }

    if (typeof contextTableId === 'number') {
      params.context_table_id = contextTableId;
    }

    return this.apiService
      .getRequest(`growelab/pro-forms/${formId}/submissions`, params)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getSubmissionsByContext(
    contextTable: ProFormContext,
    contextTableId: number
  ) {
    const params = {
      per_page: 1000,
      context_table: contextTable,
      context_table_id: contextTableId,
    };

    return this.apiService
      .getRequest('growelab/pro-forms/submissions-by-context', params)
      .pipe(catchError((err) => throwError(() => err)));
  }

  resetCurrentSubmission() {
    this.CurrentSubmissionData = null;
    this.CurrentSubmissionSource.next(null);
  }

  resetNewSubmission() {
    this.NewSubmissionData = null;
    this.NewSubmissionSource.next(null);
  }

  createPayload(
    contextTable: string,
    contextTableId: number,
    answers?: CreateProformSubmissionAnswerPayload[]
  ) {
    this.NewSubmissionData = {
      context_table: contextTable,
      context_table_id: contextTableId,
      answers: answers || [],
    } as CreateProformSubmissionPayload;
    this.NewSubmissionSource.next(this.NewSubmissionData);
  }

  addAnswerToPayload(
    answer: CreateProformSubmissionAnswerPayload,
    questionType: ProformFieldTypes
  ): void {
    if (!this.NewSubmissionData) return;

    const answers = this.NewSubmissionData.answers;
    const questionId = answer.proform_form_question_id;

    const findAnswerIndex = () =>
      answers.findIndex((ans) => ans.proform_form_question_id === questionId);

    switch (questionType) {
      case ProformFieldTypes.CHECKBOX:
      case ProformFieldTypes.RUBRIC: {
        const exists = answers.some(
          (ans) =>
            ans.proform_form_question_id === questionId &&
            ans.proform_form_question_answer_option_id ===
              answer.proform_form_question_answer_option_id
        );

        if (!exists) answers.push(answer);
        break;
      }

      case ProformFieldTypes.TEXT:
      case ProformFieldTypes.TEXTAREA: {
        const index = findAnswerIndex();
        const value = (answer.text_answer || '').trim();

        if (value === '') {
          if (index !== -1) answers.splice(index, 1); // Remove empty text
        } else if (index !== -1) {
          answers[index] = answer;
        } else {
          answers.push(answer);
        }
        break;
      }

      case ProformFieldTypes.ATTACHMENT: {
        const exists = answers.some(
          (ans) =>
            ans.proform_form_question_id === questionId &&
            ans.text_answer === answer.text_answer
        );

        if (!exists) {
          answers.push(answer);
        }
        break;
      }

      default: {
        const index = findAnswerIndex();
        if (index !== -1) {
          answers[index] = answer;
        } else {
          answers.push(answer);
        }
      }
    }

    this.NewSubmissionSource.next(this.NewSubmissionData);
  }

  /**
   * Remove a single attachment answer for a given question by its
   * stored location (text_answer). Used when a user deletes one
   * attachment from a multi-file field.
   */
  removeAttachmentAnswer(questionId: number, location: string) {
    if (!this.NewSubmissionData) return;

    this.NewSubmissionData.answers = this.NewSubmissionData.answers.filter(
      (answer) =>
        !(
          answer.proform_form_question_id === questionId &&
          answer.text_answer === location
        )
    );

    this.NewSubmissionSource.next(this.NewSubmissionData);
  }

  removeAnswerFromPayload(answerOptionId: number) {
    if (this.NewSubmissionData) {
      this.NewSubmissionData.answers.forEach((answer, index) => {
        if (
          answer.proform_form_question_answer_option_id === answerOptionId &&
          this.NewSubmissionData
        ) {
          this.NewSubmissionData.answers.splice(index, 1);
          this.NewSubmissionSource.next(this.NewSubmissionData);
        }
      });
    }
  }

  /**
   * Remove all answers for a given question from the in-progress
   * payload. This is useful for field types like attachments,
   * where deleting the file should result in no answer being
   * submitted for that question.
   */
  removeAnswersByQuestionId(questionId: number) {
    if (!this.NewSubmissionData) return;

    this.NewSubmissionData.answers = this.NewSubmissionData.answers.filter(
      (answer) => answer.proform_form_question_id !== questionId
    );

    this.NewSubmissionSource.next(this.NewSubmissionData);
  }

  createSubmission(formId: number, payload: CreateProformSubmissionPayload) {
    return this.apiService
      .postRequest(`growelab/pro-forms/${formId}/submissions`, payload)
      .pipe(
        tap(() => {
          this.NewSubmissionData = null;
          this.NewSubmissionSource.next(null);
        }),
        catchError((err) => throwError(() => err))
      );
  }

  editSubmission(
    submissionId: number,
    payload: CreateProformSubmissionPayload
  ) {
    return this.apiService
      .putRequest(
        `growelab/pro-forms/form-submissions/${submissionId}`,
        payload
      )
      .pipe(
        tap(() => {
          this.NewSubmissionData = null;
          this.NewSubmissionSource.next(null);
        }),
        catchError((err) => throwError(() => err))
      );
  }

  uploadAttachmentImage(file: File, questionId: number) {
    const formData = new FormData();
    formData.append('file', file);
    return this.apiService.postFormRequest(
      `growelab/pro-forms/question/${questionId}/upload`,
      formData,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  downloadFile(location: string) {
    return this.httpClient.get(`${EnvironmentService.apiUrl()}${location}`, {
      responseType: 'blob',
    });
  }
}
